import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { LOGO_CREST } from "@/lib/assets";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { to: "/about", label: "About" },
  { label: "Ministry", dropdown: [
    { to: "/ministry", label: "Overview" },
    { to: "/watch", label: "Watch Messages" },
    { to: "/tv", label: "HBTV Channel" },
    { to: "/tv/advertise", label: "Advertise on HBTV" },
    { to: "/events", label: "Events" },
    { to: "/devotional", label: "Daily Devotional" },
    { to: "/prayer", label: "Prayer Wall" },
    { to: "/stories", label: "Stories of Faith" },
    { to: "/studio", label: "Kingdom Studio" },
    { to: "/profile", label: "Executive Profile" },
  ] },
  { to: "/tv", label: "HBTV" },
  { to: "/academy", label: "AI Academy" },
  { to: "/consulting", label: "Consulting" },
  { to: "/business", label: "Business" },
  { to: "/store", label: "Store" },
  { to: "/music", label: "Music" },
  { to: "/books", label: "Books" },
  { to: "/gallery", label: "Gallery" },
  { to: "/resources", label: "Resources" },
];

const MOBILE_LINKS = [
  { to: "/about", label: "About" },
  { to: "/ministry", label: "Ministry" },
  { to: "/watch", label: "Watch" },
  { to: "/tv", label: "HBTV Channel" },
  { to: "/tv/advertise", label: "Advertise on HBTV" },
  { to: "/events", label: "Events" },
  { to: "/devotional", label: "Daily Devotional" },
  { to: "/prayer", label: "Prayer Wall" },
  { to: "/stories", label: "Stories of Faith" },
  { to: "/studio", label: "Kingdom Studio" },
  { to: "/profile", label: "Executive Profile" },
  { to: "/academy", label: "AI Academy" },
  { to: "/consulting", label: "Consulting" },
  { to: "/business", label: "Business" },
  { to: "/store", label: "Store" },
  { to: "/music", label: "Music" },
  { to: "/books", label: "Books" },
  { to: "/gallery", label: "Gallery" },
  { to: "/resources", label: "Resources" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background,border,padding] duration-500 ${
          scrolled ? "glass py-3" : "bg-transparent py-5 border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img
              src={LOGO_CREST}
              alt="Apostle Blaise Judah crest"
              className="h-11 w-11 object-contain [mix-blend-mode:screen] transition-transform duration-500 group-hover:scale-105"
            />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-base text-white tracking-[0.08em]">BLAISE JUDAH</span>
              <span className="text-[9px] uppercase tracking-[0.3em] text-gold-500 mt-1.5">Kingdom Leadership</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {LINKS.map((l) =>
              l.dropdown ? (
                <DropdownMenu key={l.label}>
                  <DropdownMenuTrigger
                    data-testid="nav-dropdown-ministry"
                    className={`flex items-center gap-1 text-[13px] tracking-wide outline-none transition-colors duration-300 hover:text-gold-400 ${
                      l.dropdown.some((s) => s.to === location.pathname) ? "text-gold-400" : "text-white/75"
                    }`}
                  >
                    {l.label} <ChevronDown size={13} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-ink-paper border-gold-500/25 text-white min-w-48">
                    {l.dropdown.map((s) => (
                      <DropdownMenuItem key={s.to} asChild className="focus:bg-gold-500/15 focus:text-gold-300 cursor-pointer">
                        <Link to={s.to} data-testid={`nav-link-${s.label.toLowerCase().replace(/\s/g, "-")}`}>{s.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-[13px] tracking-wide transition-colors duration-300 hover:text-gold-400 ${
                    location.pathname === l.to ? "text-gold-400" : "text-white/75"
                  }`}
                >
                  {l.label}
                </Link>
              )
            )}
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => setCartOpen(true)}
              data-testid="nav-cart-btn"
              aria-label="Open cart"
              className="relative p-2 text-white/80 hover:text-gold-300 transition-colors"
            >
              <ShoppingBag size={19} />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-gold-500 text-ink text-[10px] font-bold rounded-full"
                >
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </button>
            {user ? (
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="nav-dashboard-btn"
                className="hidden sm:inline-flex items-center border border-gold-500/40 px-4 py-2 text-xs uppercase tracking-[0.15em] text-gold-300 hover:bg-gold-500 hover:text-ink transition-colors duration-300"
              >
                {user.role === "admin" ? "Dashboard" : "My Academy"}
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="hidden sm:inline-block text-sm text-white/75 hover:text-gold-400 transition-colors duration-300"
              >
                Sign in
              </Link>
            )}
            <button
              onClick={() => navigate("/academy")}
              data-testid="nav-join-academy-btn"
              className="hidden md:inline-flex items-center gap-2 bg-gold-500 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-ink font-semibold hover:bg-gold-400 active:scale-[0.98] transition-[background,transform] duration-300"
            >
              Join Academy
            </button>
            <button
              className="lg:hidden text-white p-1"
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu"
      >
        <div className="absolute inset-0 bg-ink/95 backdrop-blur-lg" onClick={() => setOpen(false)} />
        <div className="relative flex h-full flex-col justify-center px-10 gap-2 overflow-y-auto py-24">
          {MOBILE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="font-serif text-3xl text-white/90 hover:text-gold-400 transition-colors py-1.5"
            >
              {l.label}
            </Link>
          ))}
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/contact" className="text-gold-400 uppercase tracking-[0.2em] text-sm">Contact →</Link>
            <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login"} className="text-white/70 uppercase tracking-[0.2em] text-sm">
              {user ? "My Account" : "Sign in"} →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
